import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import '../../styles/error-recovery.css';

const ErrorRecovery = ({ 
  error, 
  onRetry, 
  onReportError,
  showReportButton = true,
  maxRetries = 3,
  retryDelay = 1000,
  fallbackComponent = null,
  errorBoundary = false
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [timeToRetry, setTimeToRetry] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [errorReported, setErrorReported] = useState(false);

  const handleRetry = useCallback(async () => {
    if (retryCount >= maxRetries) {
      return;
    }

    setIsRetrying(true);
    try {
      await onRetry?.();
      setRetryCount(0); // Reset on success
    } catch (retryError) {
      console.error('Retry failed:', retryError);
      setRetryCount(prev => prev + 1);
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry, retryCount, maxRetries]);

  // Auto-retry logic
  useEffect(() => {
    if (retryCount < maxRetries && retryCount > 0) {
      const delay = retryDelay * Math.pow(2, retryCount - 1); // Exponential backoff
      setTimeToRetry(delay);
      setIsRetrying(true);

      const countdownInterval = setInterval(() => {
        setTimeToRetry(prev => {
          if (prev <= 1000) {
            clearInterval(countdownInterval);
            handleRetry();
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [retryCount, maxRetries, retryDelay, handleRetry]);

  const handleManualRetry = () => {
    if (isRetrying) return;
    setRetryCount(prev => prev + 1);
  };

  const handleReportError = async () => {
    try {
      setErrorReported(true);
      await onReportError?.(error);
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
      setErrorReported(false);
    }
  };

  const getErrorMessage = () => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.error) return error.error;
    return 'An unexpected error occurred';
  };

  const getErrorType = () => {
    if (!error) return 'unknown';

    const message = getErrorMessage().toLowerCase();
    const errorName = error?.name?.toLowerCase() || '';

    if (message.includes('loading chunk') || message.includes('chunkloaderror') || errorName === 'chunkloaderror') return 'chunk';
    if (message.includes('network') || message.includes('fetch')) return 'network';
    if (message.includes('timeout')) return 'timeout';
    if (message.includes('permission') || message.includes('unauthorized')) return 'permission';
    if (message.includes('not found') || message.includes('404')) return 'notfound';
    if (message.includes('server') || message.includes('500')) return 'server';

    return 'unknown';
  };

  const getErrorIcon = () => {
    const type = getErrorType();
    switch (type) {
      case 'chunk':
        return '📦';
      case 'network':
        return '📡';
      case 'timeout':
        return '⏱️';
      case 'permission':
        return '🔒';
      case 'notfound':
        return '🔍';
      case 'server':
        return '🖥️';
      default:
        return '⚠️';
    }
  };

  const getSuggestion = () => {
    const type = getErrorType();
    switch (type) {
      case 'chunk':
        return 'A component failed to load. This usually happens after a new deployment. Please refresh the page.';
      case 'network':
        return 'Please check your internet connection and try again.';
      case 'timeout':
        return 'The request took too long. Please try again.';
      case 'permission':
        return 'You may not have permission to access this resource.';
      case 'notfound':
        return 'The requested resource could not be found.';
      case 'server':
        return 'There seems to be a server issue. Please try again later.';
      default:
        return 'Please try refreshing the page or contact support if the problem persists.';
    }
  };

  if (fallbackComponent && retryCount >= maxRetries) {
    return fallbackComponent;
  }

  return (
    <div className={`error-recovery ${errorBoundary ? 'error-recovery--boundary' : ''}`}>
      <div className="error-recovery__content">
        <div className="error-recovery__icon">
          {getErrorIcon()}
        </div>
        
        <div className="error-recovery__message">
          <h3 className="error-recovery__title">
            {errorBoundary ? 'Something went wrong' : 'Unable to load content'}
          </h3>
          <p className="error-recovery__description">
            {getErrorMessage()}
          </p>
          <p className="error-recovery__suggestion">
            {getSuggestion()}
          </p>
        </div>

        <div className="error-recovery__actions">
          {retryCount < maxRetries && (
            <button
              className={`error-recovery__button error-recovery__button--primary ${isRetrying ? 'loading' : ''}`}
              onClick={handleManualRetry}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <>
                  <span className="spinner"></span>
                  {timeToRetry > 0 ? `Retrying in ${Math.ceil(timeToRetry / 1000)}s...` : 'Retrying...'}
                </>
              ) : (
                `Retry ${retryCount > 0 ? `(${retryCount}/${maxRetries})` : ''}`
              )}
            </button>
          )}

          {retryCount >= maxRetries && (
            <button
              className="error-recovery__button error-recovery__button--secondary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          )}

          {showReportButton && (
            <button
              className="error-recovery__button error-recovery__button--outline"
              onClick={handleReportError}
              disabled={errorReported}
            >
              {errorReported ? 'Reported ✓' : 'Report Issue'}
            </button>
          )}
        </div>

        <div className="error-recovery__details">
          <button
            className="error-recovery__details-toggle"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </button>
          
          {showDetails && (
            <div className="error-recovery__details-content">
              <h4>Error Details:</h4>
              <div className="error-recovery__error-info">
                <div><strong>Type:</strong> {getErrorType()}</div>
                <div><strong>Time:</strong> {new Date().toLocaleString()}</div>
                <div><strong>Retry Count:</strong> {retryCount}/{maxRetries}</div>
                {error?.stack && (
                  <div className="error-recovery__stack">
                    <strong>Stack:</strong>
                    <pre>{error.stack}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {retryCount >= maxRetries && (
          <div className="error-recovery__support">
            <p>Still having trouble? <a href="mailto:support@example.com">Contact Support</a></p>
          </div>
        )}
      </div>
    </div>
  );
};

ErrorRecovery.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.instanceOf(Error)
  ]).isRequired,
  onRetry: PropTypes.func,
  onReportError: PropTypes.func,
  showReportButton: PropTypes.bool,
  maxRetries: PropTypes.number,
  retryDelay: PropTypes.number,
  fallbackComponent: PropTypes.node,
  errorBoundary: PropTypes.bool
};

export default ErrorRecovery;
